import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { computed, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';

// The barrel is imported before `StepGanttHeader` deliberately: importing `Space` from
// `'../../app/entities/space'` directly (as this file used to) bypasses `entities/index.ts`'s
// export order, and `step-gantt-header.tsx` reaches `Step` directly — in an isolated module
// graph, such as this file's own unit test, that races `shooting-day.ts` extending `Step` before
// its class body has finished running. Importing the barrel first forces `step.ts` to load, in
// order, ahead of `shooting-day.ts`. See `project-calendar.route.tsx` for the same fix.
import { Space } from '../../app/entities';
import { GanttChart } from '../../components/gantt/gantt-chart';
import { StepGanttHeader } from '../project/components/step-gantt-header';
import { StageHeader, StageNumber } from '../../features/process/components/stage-header';
import { PageLoader } from '../../app/components/page-loader';
import { Header } from '../../app/components/header';
import { useCurrentMember } from '../../hooks/use-current-member';
import { uiStore } from '../../core/stores/ui-store';
import { getDone } from '../../lib/utils/math';
import { fetchCalendarData } from '../../features/spaces/stores/space.slice';
import { toastPool } from '../../features/toasts/models/toast-pool';
import { Toast } from '../../features/toasts/models/toast';
import styles from './multi-projects-calendar.module.scss';

import type { Project } from '../../app/entities/project';
import type { Stage } from '../../app/entities/stage';
import type { Step } from '../../app/entities/step';
import type { GanttItem, GanttPatch, Row } from '../../components/gantt/types';

export type SpaceRowData =
  | { kind: 'project'; project: Project; completed: number; total: number }
  | { kind: 'stage'; stage: Stage; index: number; completed: number; total: number }
  | { kind: 'step'; step: Step; isViewOnly?: boolean };

/**
 * A project carries no dates of its own — its bar is entirely derived from its stages'
 * (`bar-layout.ts`'s `descendantSpanOf`), matching what the old chart already did by giving
 * project rows `subItems` and no `startDate`/`dueDate`.
 */
export const toSpaceGanttItems = (projects: Project[], isViewOnly?: boolean): GanttItem[] =>
  projects.map((project) => {
    // TODO: get from project entity
    const { sum, done } = getDone(project.stages);

    return {
      id: project._id,
      name: project.name,
      data: { kind: 'project' as const, project, completed: done, total: sum },
      children: project.stages
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((stage, index) => ({
          id: stage._id,
          name: stage.name,
          // PlainDate crosses the boundary as-is: no toLocalDate here, and no fromLocalDate on
          // the way back. Removing that round trip is what closes the PRO-1069 class of defect.
          ...(stage.startDate && { startDate: stage.startDate }),
          ...(stage.dueDate && { dueDate: stage.dueDate }),
          data: {
            kind: 'stage' as const,
            stage,
            index,
            completed: stage.completedSteps ?? 0,
            total: stage.totalSteps ?? 0,
          },
          children: (stage.steps ?? []).map((step) => ({
            id: step._id,
            name: step.displayName,
            isDone: step.done,
            ...(step.startDate && { startDate: step.startDate }),
            ...(step.dueDate && { dueDate: step.dueDate }),
            data: { kind: 'step' as const, step, isViewOnly },
          })),
        })),
    };
  });

/**
 * Parity with the current page: every project but the first starts collapsed
 * (`multi-projects-calendar.page.tsx:55` on the old contract), and every stage starts collapsed
 * (`:74`). Feeds `config.defaultCollapsed`, which `useGanttState` re-applies as ids it hasn't
 * seen before appear in a recomputed value of this — collapse still has exactly one owner from
 * then on, the chart itself, which never revisits an id once seeded.
 */
export const initialCollapsedFor = (items: GanttItem[]): ReadonlySet<string> => {
  const collapsed = new Set<string>();

  items.forEach((project, index) => {
    if (index !== 0) collapsed.add(project.id);
    project.children?.forEach((stage) => collapsed.add(stage.id));
  });

  return collapsed;
};

// Module scope deliberately: a component declared inside a render body gets a new function
// identity every render, and `row-header.tsx` keys off that identity as the element TYPE, not a
// prop — so every visible row's header would fully unmount and remount on any MobX-observed
// change (e.g. a teammate's remote edit on another project in the space), closing whatever
// dropdown a viewer had open on an unrelated row. `isViewOnly` can't be a closure over here, so
// it travels through `SpaceRowData` instead, set once per item by `toSpaceGanttItems`.
export const RowHeaderRenderer: React.FC<{ row: Row }> = observer(({ row }) => {
  const data = row.item.data as SpaceRowData;

  if (data.kind === 'step') {
    return <StepGanttHeader step={data.step} isViewOnly={data.isViewOnly} />;
  }

  if (data.kind === 'stage') {
    return (
      <StageHeader
        name={data.stage.name}
        suffix={<StageNumber isActive>{(data.index + 1).toString().padStart(2, '0')}</StageNumber>}
        total={data.total}
        completed={data.completed}
        isActive
      />
    );
  }

  return (
    <StageHeader
      name={data.project.name}
      suffix={
        <img
          className={styles['projectImage']}
          src={data.project.avatar || '/images/project_cover_placeholder.jpg'}
          alt={data.project.name}
        />
      }
      total={data.total}
      completed={data.completed}
      isActive
    />
  );
});

// Module scope for the same reason as `RowHeaderRenderer` above — `group-bar.tsx` renders this
// as an element TYPE, so a fresh identity per render would remount every visible group bar.
export const GroupBarSuffix: React.FC<{ row: Row }> = observer(({ row }) => {
  const data = row.item.data as SpaceRowData;

  // Parity with the old chart: `ParentStrip` drew `nameSuffix`, and only stages carried one here
  // — a project's badge was `headerSuffix`, which the SIDEBAR read and the bar never did.
  if (data.kind !== 'stage') return null;

  return <StageNumber isActive>{(data.index + 1).toString().padStart(2, '0')}</StageNumber>;
});

/**
 * `GanttChart`'s `onCommit` (spec §3.2), space-route counterpart of
 * `project-calendar.route.tsx`'s `commitGanttPatches` — same optimistic-apply/await/revert-whole-
 * set contract, just searching across every project's stages instead of one project's. See that
 * function's doc-comment for why the shape survives a batch of more than one patch (the
 * dependency work) and why there is no `Date` conversion here (PRO-1069).
 *
 * `useBarDrag`/`useBarKeyboard` swallow their own rejection and expose no error channel (Task
 * 17's review), so this function is what actually surfaces a failed save — via the same
 * `toastPool`/`Toast` pair `Step`'s own error handling already uses (`app/entities/step.ts`).
 */
export const commitSpaceGanttPatches = async (
  projects: Project[],
  patches: GanttPatch[],
): Promise<void> => {
  const steps = projects.flatMap((project) => project.stages.flatMap((stage) => stage.steps ?? []));
  const targets = patches
    .map((patch) => ({ patch, step: steps.find((step) => step._id === patch.id) }))
    .filter((target): target is { patch: GanttPatch; step: Step } => !!target.step);

  // M2. An id that resolves to no `Step` — a step a teammate deleted or moved out from under
  // this drag — used to be a bare `return`: no write, no `step.update`, no toast, and the bar
  // snapping back to its old dates with nothing to explain why. That is the one failure on this
  // path the user could not see, in direct contrast to the rejection handler below, which does
  // toast. Any unresolved id now aborts the WHOLE commit and says so, rather than half-applying a
  // set whose contract (spec §3.2) is that it lands or fails together — the same all-or-nothing
  // rule the `catch` below implements for a rejected call.
  if (patches.length === 0) return;
  if (targets.length !== patches.length) {
    toastPool.insert(
      new Toast('That item no longer exists. Refresh to see the latest dates.', 'error'),
    );

    return;
  }

  const previous = targets.map(({ step }) => ({
    step,
    startDate: step.startDate,
    dueDate: step.dueDate,
  }));

  runInAction(() => {
    targets.forEach(({ patch, step }) => {
      if (patch.startDate !== undefined) step.startDate = patch.startDate;
      if (patch.dueDate !== undefined) step.dueDate = patch.dueDate;
    });
  });

  try {
    await Promise.all(
      targets.map(({ patch, step }) =>
        step.update({
          ...(patch.startDate !== undefined && { startDate: patch.startDate }),
          ...(patch.dueDate !== undefined && { dueDate: patch.dueDate }),
        }),
      ),
    );
  } catch {
    runInAction(() => {
      previous.forEach(({ step, startDate, dueDate }) => {
        step.startDate = startDate;
        step.dueDate = dueDate;
      });
    });
    toastPool.insert(new Toast('Could not save the change. Please try again.', 'error'));
  }
};

interface CalendarBodyProps {
  isLoading: boolean;
  projects?: Project[];
  isViewOnly?: boolean;
}

/** Exported for `multi-projects-calendar.test.tsx`, which drives it without the page's data fetch. */
export const MultiProjectsCalendarBody: React.FC<CalendarBodyProps> = observer(
  ({ isLoading, projects, isViewOnly }) => {
    // A MobX `computed`, not a bare `useMemo`: `toSpaceGanttItems` reads every project's,
    // stage's and step's observable fields, and `useMemo`'s own dependency check would
    // otherwise decide whether to read them at all — skip a re-run and those fields silently
    // drop out of this render's tracked dependencies, so a later change to them (a teammate
    // editing any project in the space) would never trigger a re-render. `computed` tracks
    // fully on every `.get()` regardless, and only produces a new `items` reference when a
    // tracked value actually changed.
    const items = useMemo(
      () => computed(() => toSpaceGanttItems(projects ?? [], isViewOnly)),
      [projects, isViewOnly],
    ).get();

    const defaultCollapsed = useMemo(() => initialCollapsedFor(items), [items]);

    const onCommit = useCallback(
      (patches: GanttPatch[]) => commitSpaceGanttPatches(projects ?? [], patches),
      [projects],
    );

    return (
      <div className={styles['page']}>
        <Header title="Calendar">
          <div />
        </Header>

        <div className={styles['container']}>
          {isLoading ? (
            <PageLoader />
          ) : (
            <GanttChart
              items={items}
              onCommit={onCommit}
              config={{ readOnly: isViewOnly, defaultCollapsed }}
              renderers={{ rowHeader: RowHeaderRenderer, groupBarSuffix: GroupBarSuffix }}
              primaryColor="var(--color-primary-sandal)"
              secondaryColor="var(--color-actions-main-action)"
            />
          )}
        </div>
      </div>
    );
  },
);

export const MultiProjectsCalendarPage = observer(() => {
  const { spaceId } = useParams() as { spaceId: string; projectId: string };

  const currentMember = useCurrentMember();

  const isViewOnly = currentMember?.role === 'guest';
  const [isLoading, setIsLoading] = useState(true);

  const space = Space.getOne(spaceId);
  const spaceProjects = space?.projects;

  useEffect(() => {
    fetchCalendarData(spaceId)
      .then(() => {
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [spaceId, spaceProjects]);

  useEffect(() => {
    uiStore.currentLocation = 'space.calendar';

    return () => {
      uiStore.currentLocation = '';
    };
  }, []);

  return (
    <MultiProjectsCalendarBody
      isLoading={isLoading}
      projects={spaceProjects}
      isViewOnly={isViewOnly}
    />
  );
});
